import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import {GRID_SCHEMA_CLASS_TO_TYPE_TEXT, GridSchemaClass} from 'constants/SampleConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {CreateGridSchemaParams, UpdateGridSchemaParams} from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {ContainerSchema, GridSchema} from 'samples/storables/StorableSchemaConstants';
import SchemaSettingsPageContainer, {ArchiveSchemaConfig} from 'schemas/SchemaSettingsPageContainer';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import GridSchemaSettings from './GridSchemaSettings';

class GridSchemaSettingsPageConnector extends DataConnector<GridSchemaSettingsProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [SampleSchemaStore];

  enhanceProps(): EnhancedProps {
    let newState: EnhancedProps = {
      containerSchemas: SampleSchemaStore.getContainerSchemas(this.props.registryId),
      savingState: SampleSchemaStore.getSavingState(),
    };
    if (this.props.gridSchemaId) {
      const gridSchema = SampleSchemaStore.getGridSchema(this.props.gridSchemaId, this.props.gridSchemaClass);
      newState = {...newState, gridSchema};
    }
    return newState;
  }
}

export interface GridSchemaSettingsProps {
  gridSchemaClass: GridSchemaClass;
  gridSchemaId?: string;
  ownerUrlId: string;
  registryId: string;
}
export interface EnhancedProps {
  containerSchemas?: Array<ContainerSchema> | null;
  gridSchema?: GridSchema | null;
  savingState: SavingState;
}

interface QueryingProps {
  isWritable: boolean;
}

export class GridSchemaSettingsPage extends React.Component<
  GridSchemaSettingsProps & EnhancedProps & QueryingProps
> {
  override componentDidMount(): void {
    this.fetchGridSchemas();
  }

  override componentDidUpdate(
    prevProps: GridSchemaSettingsProps & EnhancedProps & QueryingProps,
    prevState: {}
  ): void {
    if (prevProps.gridSchemaClass !== this.props.gridSchemaClass) {
      this.fetchGridSchemas();
    }

    if (
      prevProps.savingState === SavingState.PENDING &&
      RegistryUtil.isSavingFinished(this.props.savingState)
    ) {
      SampleSchemaActions.resetSavingState();
    }
  }

  private fetchGridSchemas = (): void => {
    SampleSchemaActions.fetchAllContainerSchemas();
    SampleSchemaActions.fetchAllGridSchemas(this.props.gridSchemaClass);
  };

  private handleNavigateToGridSchemas = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getRegistryGridSchemasUrl(
        this.props.ownerUrlId,
        this.props.registryId,
        this.props.gridSchemaClass
      )
    );
  };

  private getArchiveConfig = (): ArchiveSchemaConfig | undefined => {
    if (!this.props.gridSchema || !this.props.isWritable) {
      return undefined;
    }

    return {
      onArchive: (id: string, purpose: string): void => {
        ArchiveActions.bulkArchiveStorableSchemas([id], purpose);
      },
      onUnarchive: (id: string): void => {
        ArchiveActions.bulkUnarchiveStorableSchemas([id]);
      },
      isArchivable: true,
      isUnarchivable: true,
    };
  };

  private handleCreate = (gridSchema: CreateGridSchemaParams): void => {
    SampleSchemaActions.createGridSchema(
      this.props.registryId,
      gridSchema,
      this.props.gridSchemaClass,
      this.handleNavigateToGridSchemas
    );
  };

  private handleUpdate = (gridSchema: UpdateGridSchemaParams): void => {
    assert(this.props.gridSchemaId != null, 'gridSchemaId must exist for update.');
    if (this.props.gridSchemaId) {
      SampleSchemaActions.updateGridSchema(this.props.gridSchemaId, gridSchema, this.props.gridSchemaClass);
    }
  };

  private updateWindowTitle = (): void => {
    if (this.props.gridSchema) {
      BenchlingUtil.setWindowTitle(
        `${this.props.gridSchema.name} - ${
          GRID_SCHEMA_CLASS_TO_TYPE_TEXT[this.props.gridSchemaClass]
        } Schemas`
      );
    }
  };

  override render(): JSX.Element {
    this.updateWindowTitle();

    return (
      <SchemaSettingsPageContainer
        archiveConfig={this.getArchiveConfig()}
        onNavigateToListing={this.handleNavigateToGridSchemas}
        schema={this.props.gridSchemaId && this.props.gridSchema ? this.props.gridSchema : null}
        schemaType={this.props.gridSchemaClass as unknown as ModelTypeConstants}
      >
        {(this.props.gridSchemaId && !this.props.gridSchema) || !this.props.containerSchemas ? (
          <HorizontalThrobber />
        ) : (
          <GridSchemaSettings
            containerSchemas={this.props.containerSchemas}
            gridSchema={this.props.gridSchema}
            gridSchemaClass={this.props.gridSchemaClass}
            isBeingCreated={!this.props.gridSchemaId}
            isWritable={this.props.isWritable}
            onCreate={this.handleCreate}
            onUpdate={this.handleUpdate}
            registryId={this.props.registryId}
            ownerUrlId={this.props.ownerUrlId}
            savingState={this.props.savingState}
          />
        )}
      </SchemaSettingsPageContainer>
    );
  }
}

function QueryingGridSchemaSettingsPage(props: GridSchemaSettingsProps & EnhancedProps): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  return <GridSchemaSettingsPage {...props} isWritable={isWritable} />;
}

export default GridSchemaSettingsPageConnector.connect(QueryingGridSchemaSettingsPage);
